import { coerceCompareValue } from "@/components/canvas-editor/shapes/shapes";
import { FeedParameter } from "@/lib/types";

/**
 * What values a Param Compare can usefully be compared against.
 *
 * A Param Compare's left operand is a `$PARAM` reference, and installing the
 * node substitutes the owner's value for it. So by the time an engine sees the
 * condition, BOTH operands are literals and the comparison is a constant —
 * either it is true for every post or it is false for every post. Nobody has
 * ever wanted that, which makes "can this ever be true?" a question worth
 * answering while the node is being written rather than after a feed has been
 * silently empty for a day.
 *
 * The trap this exists to close is that the answer depends on a quoting rule
 * nobody can see. `replaceCustomNodeParameters` substitutes by parameter type:
 *
 *   toggle   replaces `"$NAME"` INCLUDING the quotes  -> a JSON boolean
 *   select   replaces `$NAME` INSIDE the quotes       -> a JSON string
 *
 * so a toggle compared to `IN` compiles to `[true, "==", "IN"]` and a select
 * compared to `true` compiles to `["IN", "==", true]`. Both are well-formed,
 * both are accepted by every engine, and both are false for every post
 * forever. Reported by a user on 2026-10-01 who made one of each in a morning
 * and concluded she was the problem; a prod audit the same day found three
 * components (one of them public, one with twenty-one such comparisons) in
 * exactly that state.
 *
 * The fix is to stop offering a free-text box for a value whose space is
 * already declared. `valueSpaceFor` is the rule; `isReachable` is the same rule
 * used to judge a value that is ALREADY stored, which must be reported and
 * never silently rewritten — a node's current behaviour is not ours to change.
 *
 * Mirrored server-side in `feed-processor app/components_v2/param_compare.py`,
 * which refuses a publish for the same reason. Keep the two in step.
 */
type ParamValueSpace =
  | { kind: "free" }
  | { kind: "boolean"; parameterName: string }
  | {
      kind: "options";
      parameterName: string;
      options: string[];
      labels?: string[];
    };

const freeValueSpace: ParamValueSpace = { kind: "free" };

/**
 * The parameter name a Param Compare's left operand refers to, if it refers to
 * exactly one.
 *
 * Deliberately strict. A left operand that merely CONTAINS a reference (say
 * `prefix-$NAME`) substitutes into a larger string, and what that string can
 * equal is not something this module knows — those stay free text.
 */
function referencedParameterName(
  variable: string | undefined,
): string | undefined {
  const match = /^\$([A-Za-z0-9_]+)$/.exec((variable ?? "").trim());
  return match?.[1];
}

/** The declared parameter a Param Compare's left operand refers to. */
export function referencedParameter(
  variable: string | undefined,
  parameters: FeedParameter[] | undefined,
): FeedParameter | undefined {
  const name = referencedParameterName(variable);
  if (!name) return undefined;
  return parameters?.find((parameter) => parameter.name === name);
}

/**
 * The values a comparison against `parameter` could ever match.
 *
 * Only the two types with a closed, declared value space are narrowed. `input`,
 * `regex`, `number` and `list` can hold anything their owner types, so a value
 * box for them stays free text — a wrong one there is a wrong answer, not an
 * impossible one.
 */
export function valueSpaceFor(
  parameter: FeedParameter | undefined,
): ParamValueSpace {
  if (!parameter) return freeValueSpace;

  if (parameter.type === "toggle") {
    return { kind: "boolean", parameterName: parameter.name };
  }

  if (parameter.type === "select") {
    // A select with no declared options constrains nothing yet; its author is
    // probably mid-edit, and an empty dropdown would be worse than a text box.
    if (!parameter.options?.length) return freeValueSpace;
    return {
      kind: "options",
      parameterName: parameter.name,
      options: parameter.options,
      labels: parameter.labels,
    };
  }

  return freeValueSpace;
}

/** The choices to offer for `space`, as `{ value, label }` for a select. */
export function choicesFor(
  space: ParamValueSpace,
): { value: string; label: string }[] {
  if (space.kind === "boolean") {
    return [
      { value: "true", label: "true (on)" },
      { value: "false", label: "false (off)" },
    ];
  }
  if (space.kind === "options") {
    return space.options.map((option, index) => ({
      value: option,
      // The author's own label, where they gave one, because that is the word
      // the person installing the node will see next to the dropdown.
      label:
        space.labels?.[index] && space.labels[index] !== option
          ? `${space.labels[index]} (${option})`
          : option,
    }));
  }
  return [];
}

/**
 * Can a comparison against this stored value ever be true?
 *
 * `compare` is the raw string the shape holds; `makeParamCompareFilter` turns
 * `"true"`/`"false"` into JSON booleans and anything numeric into a number, and
 * this judges the value as it will be AFTER that coercion, because that is what
 * the engines compare.
 *
 * A free space answers true: not knowing is not the same as knowing it is dead.
 */
const EQUALITY_OPERATORS = ["==", "!="];

export function isReachable(
  space: ParamValueSpace,
  compare: string,
  operator: string,
): boolean {
  if (space.kind === "free") return true;
  // Only equality is judged. Seven components on prod build a numeric
  // threshold out of a select -- video sizes, hours, timezone offsets -- and
  // compare it with `>=` / `<=`, where a value outside the listed options is a
  // perfectly ordinary comparison and not a dead branch at all. Warning on
  // those would be a false alarm on live, healthy nodes.
  if (!EQUALITY_OPERATORS.includes(operator)) return true;
  const value = coerceCompareValue((compare ?? "").trim());
  if (space.kind === "boolean") return typeof value === "boolean";
  // A select substitutes a STRING, so an option the coercion turns into a
  // boolean or a number can never equal it -- which is why membership is
  // checked on the coerced value and not on what was typed.
  return typeof value === "string" && space.options.includes(value);
}

/**
 * Why a stored value can never match, phrased for the person who wrote it.
 *
 * Names the mechanism rather than the symptom: "a toggle is true or false" is
 * actionable, "invalid value" is not.
 */
export function unreachableReason(
  space: ParamValueSpace,
  compare: string,
  operator: string,
): string | undefined {
  if (isReachable(space, compare, operator)) return undefined;
  const typed = (compare ?? "").trim();
  const shown = JSON.stringify(typed);
  // `!=` against a value the parameter can never hold is always TRUE, not
  // always false -- the branch runs for every post instead of none. Both are
  // certainly unintended; only the wording differs.
  const effect =
    operator === "!="
      ? "This comparison is true for every post, so the condition does nothing."
      : "This comparison cannot match any post.";

  if (space.kind === "boolean") {
    return `$${space.parameterName} is a toggle, so it is always true or false — never ${shown}. ${effect}`;
  }
  if (space.kind === "options") {
    // The option is listed and still cannot match, because the coercion above
    // turned it into a boolean or a number while the parameter substitutes a
    // string. Saying "pick one of the options" here would be a loop: every
    // option has the same problem, and the fix is to the PARAMETER.
    if (space.options.includes(typed)) {
      return `$${space.parameterName} offers ${shown}, but a value like that is read as a yes/no or a number rather than as text, and this parameter always substitutes text — so they can never be equal. Make $${space.parameterName} a toggle, or give its options names that are not true, false or numbers.`;
    }
    return `$${space.parameterName} can only be ${space.options
      .map((option) => JSON.stringify(option))
      .join(", ")} — never ${shown}. ${effect}`;
  }
  return undefined;
}
